import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { AttachmentModel } from '@models/attachment'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/attachment/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/attachment/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/attachment/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/attachment/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/attachment/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/attachment/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/attachment/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/attachment/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/attachment/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/attachment/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/attachment/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/attachment/DELETE_FAILURE'

export type AttachmentModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: AttachmentModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadAttachments(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().attachments.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/attachments.json', options)
      .then(({ data }: { data: AttachmentModel[] }) => {
        const normalizedJson = normalize(data, Schemas.ATTACHMENT_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadAttachment(attachmentId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.attachments.loadedForKeys]
    const loadedIds = [...state.attachments.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (attachmentId && !loadedIds.includes(attachmentId)){
      loadedIds.push(attachmentId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/attachments/${attachmentId}.json`, options)
      .then(({ data }: { data: AttachmentModel }) => {
        const normalizedJson = normalize(data, Schemas.ATTACHMENT)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createAttachment(attachment: Partial<AttachmentModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      attachment,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/attachments.json', options, config)
      .then(({ data }: { data: AttachmentModel }) => {
        const normalizedJson = normalize(data, Schemas.ATTACHMENT)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateAttachment(
  attachment: UpdateParams<AttachmentModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      attachment,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/attachments/${attachment.id}.json`, options, config)
      .then(({ data }: { data: AttachmentModel }) => {
        const normalizedJson = normalize(data, Schemas.ATTACHMENT)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteAttachment(attachment: DeleteParams<AttachmentModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/attachments/${attachment.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.ATTACHMENT)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })
    return promise
  }
}

export function generateAttachmentUrl(attachmentId: number, options: EntityOptions = {}) {
  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/attachments/${attachmentId}/generate_attachment_url.json`, options)
      .then(({ data }: { data: Partial<AttachmentModel> }) => {
        const normalizedJson = normalize(data, Schemas.ATTACHMENT)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export type CopyParams = {
  attachmentIds: number[],
  orderItemId: number,
}

export function copyToCampaign(copyParams: CopyParams, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      attachment_ids: copyParams.attachmentIds,
      order_item_id: copyParams.orderItemId,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/attachments/copy_to_campaign.json', options, config)
      .then(({ data }: { data: AttachmentModel[] }) => {
        const addDataJson = data.map((attachment) => {
          const { data: attachmentData } = attachment

          return {
            ...attachment,
            data: { ...attachmentData, imported: true },
          }
        })

        const normalizedJson = normalize(addDataJson, Schemas.ATTACHMENT_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

type DownloadParams = {
  order_item_id: number,
  attachment_ids: number[],
}
export function prepareDownload(downloadParams: DownloadParams) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      ...downloadParams,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api('/attachments/prepare_download.json', {}, config)
      .then(({ data }: { data: { queued: boolean } }) => {
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: AttachmentModuleState = initialState,
  action: Action = { type: '' },
): AttachmentModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
